import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    makeStyles,
    Paper,
    Stepper,
    Step,
    StepLabel,
    Button,
    Typography,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

// Importar os steps (serão criados a seguir)
import CampaignNameStep from "./CampaignNameStep";
import CampaignImportStep from "./CampaignImportStep";
import CampaignTemplateStep from "./CampaignTemplateStep";
import CampaignReviewStep from "./CampaignReviewStep";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    stepper: {
        padding: theme.spacing(3, 0, 5),
    },
    content: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    buttons: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: "auto",
    },
    button: {
        marginLeft: theme.spacing(1),
    },
}));

const steps = ["Dados Básicos", "Importar Contatos", "Mensagem", "Revisão e Agendamento"];

const EmailCampaignWizard = () => {
    const classes = useStyles();
    const history = useHistory();
    const { campaignId } = useParams();

    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [campaign, setCampaign] = useState({
        name: "",
        sendWindowStart: "08:00",
        sendWindowEnd: "18:00",
        templateId: "",
        subject: "",
        scheduledAt: new Date(),
        status: "draft",
        totalContacts: 0
    });

    useEffect(() => {
        if (campaignId) {
            fetchCampaign(campaignId);
        }
    }, [campaignId]);

    const fetchCampaign = async (id) => {
        try {
            const { data } = await api.get(`/email-campaigns/${id}`);
            setCampaign(prevState => ({ ...prevState, ...data }));
        } catch (err) {
            toast.error("Erro ao carregar campanha");
        }
    };

    const handleNext = async () => {
        setLoading(true);
        try {
            if (activeStep === 0) {
                // Criar ou atualizar dados básicos
                if (campaign.id) {
                    // Se já tem ID, update não é necessário a menos que mudamos a API para update parcial
                    // Mas como CreateService é só POST, não usamos update pro nome ainda.
                    // Para simplificar, vou assumir criação no step 0 e update nos outros
                    // Se for edição de nome no futuro, precisaremos de update no backend
                } else {
                    const { data } = await api.post("/email-campaigns", {
                        name: campaign.name,
                        sendWindowStart: campaign.sendWindowStart,
                        sendWindowEnd: campaign.sendWindowEnd
                    });
                    setCampaign(prevState => ({ ...prevState, ...data }));
                    toast.success("Campanha criada com sucesso!");
                }
            }

            if (activeStep === 2) {
                // Atualizar Template e Assunto
                await api.put(`/email-campaigns/${campaign.id}`, {
                    templateId: campaign.templateId,
                    subject: campaign.subject
                });
            }

            if (activeStep === 3) {
                // Agendar
                await api.post(`/email-campaigns/${campaign.id}/schedule`, {
                    scheduledAt: campaign.scheduledAt
                });
                toast.success("Campanha agendada com sucesso!");
                history.push("/email-campaigns");
                return;
            }

            setActiveStep(activeStep + 1);
        } catch (err) {
            toast.error(err.response?.data?.error || "Ocorreu um erro ao avançar.");
        }
        setLoading(false);
    };

    const handleBack = () => {
        setActiveStep(activeStep - 1);
    };

    const updateCampaignState = (key, value) => {
        setCampaign(prev => ({ ...prev, [key]: value }));
    };

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <CampaignNameStep
                        campaign={campaign}
                        onChange={updateCampaignState}
                    />
                );
            case 1:
                return (
                    <CampaignImportStep
                        campaign={campaign}
                        onUploadSuccess={(stats) => {
                            setCampaign(prev => ({ ...prev, totalContacts: stats.totalContacts || stats.valid }));
                            fetchCampaign(campaign.id); // Recarregar para garantir
                        }}
                    />
                );
            case 2:
                return (
                    <CampaignTemplateStep
                        campaign={campaign}
                        onChange={updateCampaignState}
                    />
                );
            case 3:
                return (
                    <CampaignReviewStep
                        campaign={campaign}
                        onChange={updateCampaignState}
                    />
                );
            default:
                return <Typography>Passo desconhecido</Typography>;
        }
    };

    return (
        <MainContainer>
            <MainHeader>
                <Title>
                    {campaign.id ? `Editando Campanha: ${campaign.name}` : "Nova Campanha de E-mail"}
                </Title>
            </MainHeader>

            <Paper className={classes.mainPaper}>
                <Stepper activeStep={activeStep} className={classes.stepper}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <div className={classes.content}>
                    {getStepContent(activeStep)}
                </div>

                <div className={classes.buttons}>
                    {activeStep !== 0 && (
                        <Button onClick={handleBack} className={classes.button}>
                            Voltar
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        className={classes.button}
                        disabled={loading}
                    >
                        {activeStep === steps.length - 1 ? "Agendar Campanha" : "Próximo"}
                    </Button>
                </div>
            </Paper>
        </MainContainer>
    );
};

export default EmailCampaignWizard;
